import * as THREE from "three";
import { brand as defaultBrand } from "../config/brand.js";

export class Scene3D {
  constructor(scene, camera, brandConfig = defaultBrand) {
    this.scene = scene;
    this.camera = camera;
    this.brand = brandConfig;
    this.objects = [];
    this.createSceneElements();
  }

  createSceneElements() {
    const { colors } = this.brand.theme;
    const { heroObject, wireframe, grid } = this.brand.scene;

    // The hero object is intentionally simple so teams can swap it for SVG/GLB brand assets.
    const geometry = new THREE.IcosahedronGeometry(
      heroObject.radius,
      heroObject.detail,
    );
    const material = new THREE.MeshStandardMaterial({
      color: colors.primary,
      metalness: heroObject.metalness,
      roughness: heroObject.roughness,
      wireframe: false,
    });

    this.mainObject = new THREE.Mesh(geometry, material);
    this.mainObject.castShadow = true;
    this.mainObject.receiveShadow = true;
    this.scene.add(this.mainObject);
    this.objects.push(this.mainObject);

    // Create wireframe overlay
    const wireframeMaterial = new THREE.MeshBasicMaterial({
      color: colors.secondary,
      wireframe: true,
      transparent: true,
      opacity: wireframe.opacity,
    });

    this.wireframeObject = new THREE.Mesh(geometry, wireframeMaterial);
    this.wireframeObject.scale.set(
      wireframe.scale,
      wireframe.scale,
      wireframe.scale,
    );
    this.scene.add(this.wireframeObject);
    this.objects.push(this.wireframeObject);

    // Create particles
    this.createParticles();

    // Create grid helper
    this.gridHelper = new THREE.GridHelper(
      grid.size,
      grid.divisions,
      colors.secondary,
      colors.grid,
    );
    this.gridHelper.position.y = grid.y;
    this.scene.add(this.gridHelper);
    this.objects.push(this.gridHelper);
  }

  createParticles() {
    const { colors } = this.brand.theme;
    const { particles } = this.brand.scene;
    const particleCount = particles.count;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount * 3; i += 3) {
      positions[i] = (Math.random() - 0.5) * particles.spread;
      positions[i + 1] = (Math.random() - 0.5) * particles.spread;
      positions[i + 2] = (Math.random() - 0.5) * particles.spread;
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: colors.secondary,
      size: particles.size,
      transparent: true,
      opacity: particles.opacity,
    });

    this.particles = new THREE.Points(geometry, material);
    this.scene.add(this.particles);
    this.objects.push(this.particles);
  }

  update() {
    // Update all objects
    this.objects.forEach((obj) => {
      if (obj.update) obj.update();
    });
  }

  getMainObject() {
    return this.mainObject;
  }

  getWireframeObject() {
    return this.wireframeObject;
  }

  getParticles() {
    return this.particles;
  }

  applyBrandConfig(brandConfig) {
    this.brand = brandConfig;

    const { colors } = this.brand.theme;
    const { heroObject, particles, wireframe } = this.brand.scene;

    this.mainObject.material.color.set(colors.primary);
    this.mainObject.material.metalness = heroObject.metalness;
    this.mainObject.material.roughness = heroObject.roughness;

    this.wireframeObject.material.color.set(colors.secondary);
    this.wireframeObject.material.opacity = this.wireframeObject.material
      .opacity
      ? Math.min(this.wireframeObject.material.opacity, wireframe.activeOpacity)
      : wireframe.opacity;

    this.particles.material.color.set(colors.secondary);
    this.particles.material.size = particles.size;
    this.particles.material.opacity = particles.opacity;
  }

  clear() {
    this.objects.forEach((obj) => {
      this.scene.remove(obj);
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose());
        } else {
          obj.material.dispose();
        }
      }
    });
    this.objects = [];
  }

  dispose() {
    this.clear();
  }
}
